import React, { useEffect, useState } from "react";
import { Loader2, Download, Phone, Mail, RefreshCw, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { adminApi, downloadLeadsCsv } from "../../lib/adminApi";
import { resolveMediaUrl } from "../../lib/mediaUrl";

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch (_e) {
        return iso;
    }
};

const extractEmail = (msg) => {
    if (!msg) return "";
    const m = msg.match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
    return m ? m[0] : "";
};

export const AdminLeadsPage = () => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            const { data } = await adminApi.get("/admin/leads");
            setLeads(data);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Failed to load leads");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
    }, []);

    const onExport = async () => {
        setExporting(true);
        try {
            await downloadLeadsCsv();
            toast.success("Leads exported");
        } catch (_e) {
            toast.error("Export failed");
        } finally {
            setExporting(false);
        }
    };

    return (
        <div data-testid="manager-leads" className="p-6 md:p-10 max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
                <div>
                    <h1 className="font-poppins font-semibold text-white text-2xl md:text-3xl tracking-tight">
                        Leads
                    </h1>
                    <p className="mt-2 font-inter text-sm text-zinc-400">
                        All leads captured from popups, contact forms, and consultation requests.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={reload}
                        disabled={loading}
                        data-testid="leads-refresh-btn"
                        className="inline-flex items-center gap-2 border border-white/10 hover:border-white/30 text-zinc-300 hover:text-white font-inter text-sm px-4 py-2.5 rounded-full transition-colors"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                        Refresh
                    </button>
                    <button
                        type="button"
                        onClick={onExport}
                        disabled={exporting || leads.length === 0}
                        data-testid="leads-export-csv-btn"
                        className="inline-flex items-center gap-2 bg-gold hover:bg-[#C5A030] disabled:opacity-50 text-black font-medium font-inter text-sm px-5 py-2.5 rounded-full transition-colors"
                    >
                        {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                        Export CSV
                    </button>
                </div>
            </div>

            <div className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden">
                {loading ? (
                    <div className="p-10 flex items-center gap-3 text-zinc-500">
                        <Loader2 size={16} className="animate-spin" /> Loading leads…
                    </div>
                ) : leads.length === 0 ? (
                    <div className="p-12 text-center font-inter text-zinc-400">
                        No leads captured yet. They'll appear here as soon as customers fill the form.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left" data-testid="leads-table">
                            <thead>
                                <tr className="border-b border-white/10 font-inter text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                                    <th className="px-5 py-4">Name</th>
                                    <th className="px-5 py-4">Phone</th>
                                    <th className="px-5 py-4">Email</th>
                                    <th className="px-5 py-4 hidden md:table-cell">Business</th>
                                    <th className="px-5 py-4 hidden md:table-cell">Requirement</th>
                                    <th className="px-5 py-4 hidden lg:table-cell">Details</th>
                                    <th className="px-5 py-4 hidden lg:table-cell">Source</th>
                                    <th className="px-5 py-4 hidden lg:table-cell">File</th>
                                    <th className="px-5 py-4">Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leads.map((l) => {
                                    const email = l.email || extractEmail(l.message);
                                    const details = [
                                        l.quantity && `Qty: ${l.quantity}`,
                                        l.specifications && `Specs: ${l.specifications}`,
                                        l.deadline && `Deadline: ${l.deadline}`,
                                        l.budget && `Budget: ${l.budget}`,
                                    ]
                                        .filter(Boolean)
                                        .join(" · ");
                                    return (
                                        <tr
                                            key={l.id}
                                            data-testid={`lead-row-${l.id}`}
                                            className="border-b border-white/5 last:border-b-0 hover:bg-white/[0.02]"
                                        >
                                            <td className="px-5 py-4">
                                                <div className="font-poppins font-medium text-white text-sm">{l.name}</div>
                                            </td>
                                            <td className="px-5 py-4">
                                                <a
                                                    href={`tel:${l.phone}`}
                                                    className="inline-flex items-center gap-1.5 font-inter text-sm text-zinc-300 hover:text-gold"
                                                >
                                                    <Phone size={12} className="text-gold" />
                                                    {l.phone}
                                                </a>
                                            </td>
                                            <td className="px-5 py-4">
                                                {email ? (
                                                    <a
                                                        href={`mailto:${email}`}
                                                        className="inline-flex items-center gap-1.5 font-inter text-sm text-zinc-300 hover:text-gold"
                                                    >
                                                        <Mail size={12} className="text-gold" />
                                                        {email}
                                                    </a>
                                                ) : (
                                                    <span className="text-zinc-600 text-sm">—</span>
                                                )}
                                            </td>
                                            <td className="px-5 py-4 hidden md:table-cell font-inter text-sm text-zinc-300">
                                                {l.business_name || "—"}
                                            </td>
                                            <td className="px-5 py-4 hidden md:table-cell">
                                                <span className="inline-flex font-inter text-xs text-gold bg-gold/10 border border-gold/20 px-2.5 py-1 rounded-full">
                                                    {l.requirement}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4 hidden lg:table-cell font-inter text-xs text-zinc-400 max-w-[160px]">
                                                <span className="line-clamp-2" title={`${details}${l.message ? ` · ${l.message}` : ""}`}>
                                                    {details || l.message || "—"}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4 hidden lg:table-cell font-inter text-xs text-zinc-500">
                                                {l.source || "site"}
                                            </td>
                                            <td className="px-5 py-4 hidden lg:table-cell">
                                                {l.attachment_url ? (
                                                    <a
                                                        href={resolveMediaUrl(l.attachment_url)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1.5 text-gold hover:underline font-inter text-xs"
                                                        data-testid={`lead-attachment-${l.id}`}
                                                    >
                                                        <Paperclip size={12} /> View
                                                    </a>
                                                ) : (
                                                    <span className="text-zinc-600 text-xs">—</span>
                                                )}
                                            </td>
                                            <td className="px-5 py-4 font-inter text-xs text-zinc-500 whitespace-nowrap">
                                                {formatDate(l.created_at)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="mt-4 font-inter text-xs text-zinc-600">
                {leads.length > 0 && `${leads.length} ${leads.length === 1 ? "lead" : "leads"} total`}
            </div>
        </div>
    );
};

export default AdminLeadsPage;
